import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import {
  noBarData,
  noData,
  validLineData,
  totalActivity,
  dailyActivity,
  lineChartTitles,
  createDateRange,
  chartColorScale,
  getFormattedTimeStringFromPeriod,
} from "features/Library/Common/utils/performanceUtils";
import { ChartWrapper, HorizontalBarChart, LineChart } from "components/charts";
import CardReporting from "components/cards/CardReporting/CardReporting";
import { type ColumnOption } from "interfaces/columnOptions";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import { TextTruncate } from "components";
import { Tooltip } from "components/common/tooltip";
import { type CommonAccountReportProps } from "../../types";
import { sharedAccountReportingLineProps, sharedAccountReportingHorizontalBarProps } from "../../shared";
import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { bindAction } from "interfaces";
import { setExportAction, setHasDataToExport } from "features/Reporting/state/export/exportSlice";
import { type ConnectedProps, connect } from "react-redux";
import { setIsReportEnabled, reset } from "features/Reporting/state/toolbar/toolbarSlice";
import { bindActionCreators } from "@reduxjs/toolkit";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { FeatureFlags } from "featureFlags";
import { ReportUnavailable } from "components/reportUnavailable/ReportUnavailable";
import { doMessagesExport } from "features/Reporting/state/content/contentActions";
import { EntityType } from "features/Library/Flows/Designer/types";
import { useChartPeriodMeasure } from "hooks/useChartPeriodMeasure";
import { fetchMessagesOverview, useMessageEngagementQuery, useMessageLineChartQuery } from "./Queries/queries";
import "features/Library/Common/utils/performanceSCSSUtils.scss";
import styles from "../../contentOverviewReports.module.scss";
import messagesStyles from "./messagesOverviewReport.module.scss";
import classNames from "classnames";
import { useReportingFormatWithTimeZone } from "hooks/useReportingFormatWithTimeZone";
import { type MessageOverviewDetails } from "features/Reporting/types/content";
import {
  type ExportAvailability,
  GenericPerformanceListWithServerPagination,
} from "../../shared/GenericPerformanceListWithServerPagination";
import { type PaginationParams } from "features/Library/Common/models";
import { AddOnIds } from "enums/addOns";
import { useAddOnQuery } from "../../queries/useAddOnQuery";

export type Props = CommonAccountReportProps & PropsFromRedux;

export const MessageOverviewReport: FC<Props> = ({
  filter,
  setSelectedContent,
  setHasExportData,
  setExportAction,
  messagesExport,
  setIsReportEnabled,
  resetIsReportEnabled,
  accountId,
}) => {
  const [chartState, setChartState] = useState(totalActivity);
  const reportEnabled = useFeatureFlag(FeatureFlags.MessagesReport);
  const formatDateTime = useReportingFormatWithTimeZone();

  const dateRange = useMemo(() => createDateRange(filter.dateFrom, filter.dateTo), [filter.dateFrom, filter.dateTo]);

  const [chartPeriod, measureRef] = useChartPeriodMeasure(
    dateRange,
    sharedAccountReportingLineProps.margins!.left + sharedAccountReportingLineProps.margins!.right,
  );

  const lineChartQuery = useMessageLineChartQuery(filter, chartPeriod, reportEnabled);

  const { data: addons } = useAddOnQuery(accountId);
  const hasCreateAccount = addons?.find((addon) => addon.id === AddOnIds.CreateAccounts)?.isEnabled;
  const engagementQuery = useMessageEngagementQuery(filter, hasCreateAccount, reportEnabled);

  const messagesQueryKey = useMemo(() => ["MessageOverviewTable", filter], [filter]);

  const fetchMessagesData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) => fetchMessagesOverview(filter, params, signal),
    [filter],
  );

  useEffect(() => {
    setExportAction({
      method: messagesExport,
      args: [filter],
      isExportEnabled: true,
    });
  }, [filter, messagesExport, setExportAction]);

  useEffect(() => {
    setIsReportEnabled(reportEnabled);

    return () => {
      resetIsReportEnabled();
    };
  }, [reportEnabled, resetIsReportEnabled, setIsReportEnabled]);

  const lineData = useMemo(() => {
    const xData = [lineChartQuery.data?.Date, lineChartQuery.data?.Date];
    if (chartState === dailyActivity) {
      return {
        xData,
        yData: [lineChartQuery.data?.Sends, lineChartQuery.data?.Clicks],
      };
    }

    return {
      xData,
      yData: [lineChartQuery.data?.CumulativeSends, lineChartQuery.data?.CumulativeClicks],
    };
  }, [chartState, lineChartQuery.data]);

  const columns: ColumnOption<MessageOverviewDetails>[] = useMemo(
    () => [
      {
        name: "Message Name",
        width: 4,
        isSortable: true,
        sortKey: "MessageName",
        render(item) {
          return (
            <Tooltip
              target={
                <button
                  className={styles.linkButton}
                  onClick={() =>
                    setSelectedContent({
                      id: item.MessageId,
                      type: EntityType.Message,
                      name: item.MessageName,
                    })
                  }
                >
                  <TextTruncate lines={2} className={styles.wordBreak}>
                    {item.MessageName}
                  </TextTruncate>
                </button>
              }
              content={item.MessageName}
              showAlways
            />
          );
        },
      },
      {
        name: "Last Activity",
        width: 4,
        isSortable: true,
        sortKey: "LastActivity",
        render: ({ LastActivity }) => formatDateTime(LastActivity),
      },
      {
        name: "Sends",
        width: 3,
        isSortable: true,
        sortKey: "Sends",
        render: ({ Sends }) => Sends.toLocaleString(),
      },
      {
        name: "Clicks",
        width: 3,
        isSortable: true,
        sortKey: "Clicks",
        render: ({ Clicks }) => Clicks.toLocaleString(),
      },
      {
        name: "CTR",
        width: 3,
        isSortable: true,
        sortKey: "ClickThroughRate",
        render: ({ ClickThroughRate }) => `${Math.round(ClickThroughRate * 100).toLocaleString()}%`,
      },
    ],
    [formatDateTime, setSelectedContent],
  );

  const updateExportDataState = useCallback(
    ({ hasDataToExport }: ExportAvailability) =>
      setHasExportData({
        hasDataToExport,
        tooltipContent: "No data available for export. There are no messages with activity data.",
      }),
    [setHasExportData],
  );

  if (!reportEnabled) {
    return <ReportUnavailable />;
  }

  return (
    <div className={styles.reportBody}>
      <div className={styles.lineChartContainer} ref={measureRef}>
        <ChartWrapper
          titles={lineChartTitles}
          showLegend
          legendLabels={["Sends", "Clicks"]}
          colorRange={chartColorScale}
          onChange={setChartState}
        >
          <RequestStatusRenderer state={lineChartQuery.status}>
            {lineChartQuery.isSuccess &&
            validLineData([lineChartQuery.data.CumulativeSends, lineChartQuery.data.CumulativeClicks]) ? (
              <LineChart
                {...sharedAccountReportingLineProps}
                {...lineData}
                colorRange={chartColorScale}
                xFormatterFunc={(d) => getFormattedTimeStringFromPeriod(d, chartPeriod, dateRange)}
              />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>

      <div className={styles.funnelChartContainer}>
        <ChartWrapper titles={["Engagement"]}>
          <RequestStatusRenderer state={engagementQuery.status}>
            {engagementQuery.isSuccess && !noBarData(...engagementQuery.data.barData.map((d) => d.value)) ? (
              <HorizontalBarChart {...sharedAccountReportingHorizontalBarProps} data={engagementQuery.data.barData} />
            ) : (
              noData(filter)
            )}
          </RequestStatusRenderer>
        </ChartWrapper>
      </div>

      <div className={classNames(styles.performanceCardSection, messagesStyles.cards)}>
        <RequestStatusRenderer state={engagementQuery.status}>
          {engagementQuery.isSuccess && <CardReporting items={engagementQuery.data.cards} />}
        </RequestStatusRenderer>
      </div>

      <div className={styles.table}>
        <GenericPerformanceListWithServerPagination
          title="Messages"
          columns={columns}
          filter={filter}
          fetchPage={fetchMessagesData}
          queryKeyBase={messagesQueryKey}
          hasDataForExport={updateExportDataState}
        />
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  setHasExportData: bindAction(setHasDataToExport, dispatch),
  messagesExport: bindActionCreators(doMessagesExport, dispatch),
  setExportAction: bindAction(setExportAction, dispatch),
  setIsReportEnabled: bindAction(setIsReportEnabled, dispatch),
  resetIsReportEnabled: bindAction(reset, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(MessageOverviewReport);
export default ConnectedComponent;
